import React, { useState } from 'react';
import axios from 'axios';
import { Form, Input, InputNumber, Button, Select, Row, Col, Spin, message } from 'antd';
import axiosBaseURL from "../api/index";
import { AuthContextValue, useAuth } from "../providers/AuthProvider";


const regions = [
  "afar", "amhara", "benushangul Gumuz", "dire dawa", "gambela", 
  "harari", "oromiya", "S.N.N.P.R", "somalia", "tigray"
];

const crop_types = ["teff", "barley", "wheat", "sorghum", "maize", "potato"];

interface AgriFinance {
  customerId: string;
  region: string;
  latitude: number;
  longitude: number;
  land_area: number;
  crop_type: string;
  yield_estimation_year: number;
  household_size: number;
  total_yearly_income?: number;
  monthly_living_expenses?:number;
  yearly_farming_costs?:number;
  size_of_saving?:number;
  outstanding_loan_size?:number;
  size_of_cultivable_land_owned_acres?:number;
  expense_for_land_lease?:number;
  size_of_farm_equipment_owned?:number;
  size_of_livestock_assets?:number;
  proximity_to_market?:number;
  dependence_on_seasonal_crop?:number;
  size_of_non_farm_annual_income?:number;
  insured?:number;
}

interface AgriFinanceReqDto {
  agriFinance: AgriFinance[];
}

const AgriFinanceIngestion: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const { user } = useAuth() as AuthContextValue; // useAuth hook returns user context
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  // State to store form data across pages
  const [formData, setFormData] = useState<any>({});   


  const nextPage = async () => {
    try {
      // Validate and get values from the current form
      const values = await form.validateFields();
      
      // Merge new values with the existing formData state
      setFormData((prevData: any) => ({ ...prevData, ...values }));
  
      // Move to the next page
      setCurrentPage(1);
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const onFinish = async (values: AgriFinance) => {
    setLoading(true);
    try {
      // Merge the last page's values with the stored formData
      const requestData: AgriFinanceReqDto = {
        agriFinance: [{ ...formData, ...values }],
      };
  
      const response = await axiosBaseURL.post('/ai-data-ingestion', requestData, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });
  
      message.success('AgriFinance data ingested successfully.');
      form.resetFields();
      setFormData({}); // Reset form data after submission
      setCurrentPage(0); // Return to first page
    } catch (error: any) {
      console.error('Full error response:', error.response);
      //message.error(error.response?.data || 'Failed to ingest AgriFinance data.');
    } finally {
      setLoading(false);
    }
  };
  
 
  const prevPage = () => {
    setCurrentPage(0); // Go to previous page
  };

  return (
    <Spin spinning={loading}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        className="flex flex-col bg-white p-4"
        style={{ marginTop: '30px' }}
      >
        {currentPage === 0 && (
          <div>
            {/* Page 1: First Half of the Form */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Customer ID" name="customerId" rules={[{ required: true, message: 'Please enter Customer ID' }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Region" name="region" rules={[{ required: true, message: 'Please select Region' }]}>
                  <Select placeholder="--select region--">
                    {regions.map((region) => (
                      <Select.Option key={region} value={region}>
                        {region}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Latitude" name="latitude" rules={[{ required: true, message: 'Please enter Latitude' }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Longitude" name="longitude" rules={[{ required: true, message: 'Please enter Longitude' }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Land Area" name="land_area" rules={[{ required: true, message: 'Please enter Land Area' }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Crop Type" name="crop_type" rules={[{ required: true, message: 'Please select Crop Type' }]}>
                  <Select placeholder="--select crop type--">
                    {crop_types.map((crop_type) => (
                      <Select.Option key={crop_type} value={crop_type}>
                        {crop_type}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Yield Estimation Year(yyyy)" name="yield_estimation_year" rules={[{ required: true, message: 'Please enter Yield Estimation Year' }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Household Size" name="household_size" rules={[{ required: true, message: 'Please enter Household Size' }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Total Yearly Income" name="total_yearly_income" rules={[{ required: true, message: 'Please enter Total Yearly Income' }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </div>
        )}

        {currentPage === 1 && (
          <div>
            {/* Page 2: Second Half of the Form */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Monthly Living Expenses" name="monthly_living_expenses" rules={[{ required: true, message: 'Please enter Monthly Living Expenses' }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Yearly Farming Costs" name="yearly_farming_costs" rules={[{ required: true, message: 'Please enter Yearly Farming Costs' }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Size of Saving" name="size_of_saving" rules={[{ required: true, message: 'Please enter Size of Saving' }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Outstanding Loan Size" name="outstanding_loan_size" rules={[{ required: true, message: 'Please enter Outstanding Loan Size' }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Size of Cultivable Land Owned (Acres)" name="size_of_cultivable_land_owned_acres" rules={[{ required: true, message: 'Please enter Size of Cultivable Land Owned' }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Expense for Land Lease" name="expense_for_land_lease" rules={[{ required: true, message: 'Please enter Expense for Land Lease' }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Size of Farm Equipment Owned" name="size_of_farm_equipment_owned" rules={[{ required: true, message: 'Please enter Size of Farm Equipment Owned' }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Size of Livestock Assets" name="size_of_livestock_assets" rules={[{ required: true, message: 'Please enter Size of Livestock Assets' }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Proximity to Market" name="proximity_to_market" rules={[{ required: true, message: 'Please enter Proximity to Market' }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Dependence on Seasonal Crop" name="dependence_on_seasonal_crop" rules={[{ required: true, message: 'Please enter Dependence on Seasonal Crop' }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Size of Non-Farm Annual Income" name="size_of_non_farm_annual_income" rules={[{ required: true, message: 'Please enter Size of Non-Farm Annual Income' }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Insured" name="insured" rules={[{ required: true, message: 'Please enter Insured' }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </div>
        )}

        <Row gutter={16}>
          <Col span={24}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              {currentPage === 1 && (
                <Button type="default" onClick={prevPage}>
                  Previous
                </Button>
              )}
              {currentPage === 0 && (
                <Button type="primary" onClick={nextPage}>
                  Next
                </Button>
              )}
              {currentPage === 1 && (
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </Form>
    </Spin>
  );
};
export default AgriFinanceIngestion;